/**
 * GridMind — Página pública de resposta (Riscos Psicossociais, NR-1).
 * Acessível por link/QR. Em campanha anônima NUNCA solicita identidade.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Brain, CheckCircle2, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PublicGridMindResponse = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [campanha, setCampanha] = useState(null);
  const [completed, setCompleted] = useState(false);

  const [respostas, setRespostas] = useState({});      // pergunta_id -> valor
  const [abertas, setAbertas] = useState({});          // pergunta_id -> texto
  const [segmento, setSegmento] = useState({ departamento: '', unidade: '', cargo: '', gestor: '' });
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    carregar();
    // eslint-disable-next-line
  }, [token]);

  const carregar = async () => {
    try {
      const r = await axios.get(`${API}/api/gridmind/public/${token}`);
      setCampanha(r.data);
      // Trava de "já respondido" só vale para campanha anônima (controle por navegador).
      // Em campanha identificada, a unicidade é garantida pelo servidor (e-mail/funcionário),
      // permitindo que outra pessoa responda no mesmo dispositivo.
      if (r.data?.anonima && localStorage.getItem(`gridmind_done_${token}`)) {
        setCompleted(true);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Pesquisa indisponível.');
    } finally {
      setLoading(false);
    }
  };

  const likertLabels = campanha?.metodologia?.likert_labels || ['Nunca', 'Raramente', 'Às vezes', 'Frequentemente', 'Sempre'];

  const todasPerguntas = () => {
    const ids = [];
    (campanha?.metodologia?.escalas || []).forEach(e =>
      (e.fatores || []).forEach(f => (f.perguntas || []).forEach(p => ids.push(p))));
    (campanha?.perguntas_customizadas || []).filter(p => p.tipo !== 'aberta').forEach(p => ids.push(p));
    return ids;
  };

  const totalObrig = () => todasPerguntas().filter(p => p.obrigatoria !== false).length;
  const respondidasObrig = () => todasPerguntas().filter(p => p.obrigatoria !== false && respostas[p.id]).length;

  // token de resposta anônima estável por (campanha, navegador): garante idempotência
  // no servidor sem nunca associar identidade ao respondente.
  const tokenRespostaAnonima = () => {
    const key = `gridmind_rt_${token}`;
    let tk = localStorage.getItem(key);
    if (!tk) {
      tk = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(key, tk);
    }
    return tk;
  };

  const enviar = async () => {
    const faltando = todasPerguntas().filter(p => p.obrigatoria !== false && !respostas[p.id]);
    if (faltando.length > 0) {
      setError(`Responda todas as ${totalObrig()} perguntas obrigatórias (faltam ${faltando.length}).`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (campanha && !campanha.anonima) {
      const emailOk = /\S+@\S+\.\S+/.test(email || '');
      if (!emailOk) {
        setError('Esta pesquisa é identificada: informe um e-mail válido para responder.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        segmento,
        respostas: Object.entries(respostas).map(([pergunta_id, valor]) => ({ pergunta_id, valor: Number(valor) })),
        respostas_abertas: Object.entries(abertas)
          .filter(([, t]) => (t || '').trim())
          .map(([pergunta_id, texto]) => ({ pergunta_id, texto })),
        origem: 'link',
      };
      if (campanha.anonima) {
        // controle de resposta única (modo anônimo): token estável por navegador/campanha,
        // persistido e reenviado para que o servidor rejeite reenvios (409) sem armazenar identidade.
        const tk = tokenRespostaAnonima();
        payload.response_token = tk;
      } else {
        payload.respondente_nome = nome;
        payload.respondente_email = email;
      }
      await axios.post(`${API}/api/gridmind/public/${token}/responder`, payload);
      // Marca conclusão local apenas em modo anônimo (trava por navegador).
      if (campanha.anonima) localStorage.setItem(`gridmind_done_${token}`, '1');
      setCompleted(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao enviar respostas.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !campanha) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-slate-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Resposta registrada!</h2>
            <p className="text-slate-600">Obrigado por contribuir com a avaliação de riscos psicossociais da sua organização.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card className="border-indigo-100">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-lg"><Brain className="w-6 h-6 text-white" /></div>
              <div>
                <CardTitle className="text-xl">{campanha.codigo}</CardTitle>
                <CardDescription>{campanha.descricao || 'Pesquisa de Riscos Psicossociais (NR-1)'}</CardDescription>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-md p-2">
              <ShieldCheck className="w-4 h-4" />
              {campanha.anonima
                ? 'Pesquisa anônima — sua identidade não é registrada.'
                : 'Pesquisa identificada — suas respostas são vinculadas ao seu cadastro.'}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Responda com sinceridade. Escala: <b>1 = {likertLabels[0]}</b> até <b>5 = {likertLabels[4]}</b>.
            </p>
            {/* Segmento (sempre coletado para análise por área, sem identificar a pessoa) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Departamento</Label><Input value={segmento.departamento} onChange={e => setSegmento({ ...segmento, departamento: e.target.value })} placeholder="Ex.: Operações" /></div>
              <div><Label>Unidade</Label><Input value={segmento.unidade} onChange={e => setSegmento({ ...segmento, unidade: e.target.value })} placeholder="Ex.: Matriz" /></div>
              <div><Label>Cargo</Label><Input value={segmento.cargo} onChange={e => setSegmento({ ...segmento, cargo: e.target.value })} placeholder="Ex.: Analista" /></div>
              <div><Label>Gestor/Área</Label><Input value={segmento.gestor} onChange={e => setSegmento({ ...segmento, gestor: e.target.value })} placeholder="Opcional" /></div>
            </div>
            {!campanha.anonima && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input value={nome} onChange={e => setNome(e.target.value)} /></div>
                <div><Label>E-mail</Label><Input value={email} onChange={e => setEmail(e.target.value)} /></div>
              </div>
            )}
          </CardContent>
        </Card>

        {error && (
          <div className="bg-red-50 text-red-700 rounded-md p-3 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Escalas e perguntas */}
        {(campanha.metodologia?.escalas || []).map(escala => (
          <Card key={escala.id}>
            <CardHeader>
              <CardTitle className="text-base">{escala.nome}</CardTitle>
              {escala.descricao && <CardDescription>{escala.descricao}</CardDescription>}
            </CardHeader>
            <CardContent className="space-y-5">
              {(escala.fatores || []).map(fator => (
                <div key={fator.id} className="space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{fator.nome}</p>
                  {(fator.perguntas || []).map(p => (
                    <Likert key={p.id} pergunta={p} labels={likertLabels}
                            valor={respostas[p.id]} onChange={v => setRespostas({ ...respostas, [p.id]: v })} />
                  ))}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Perguntas customizadas da empresa (likert) */}
        {(campanha.perguntas_customizadas || []).filter(p => p.tipo !== 'aberta').length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Perguntas da Empresa</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(campanha.perguntas_customizadas || []).filter(p => p.tipo !== 'aberta').map(p => (
                <Likert key={p.id} pergunta={p} labels={likertLabels}
                        valor={respostas[p.id]} onChange={v => setRespostas({ ...respostas, [p.id]: v })} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Perguntas abertas */}
        {(campanha.perguntas_abertas || []).length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Comentários</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(campanha.perguntas_abertas || []).map(p => (
                <div key={p.id}>
                  <Label className="text-sm">{p.texto}</Label>
                  <Textarea value={abertas[p.id] || ''} onChange={e => setAbertas({ ...abertas, [p.id]: e.target.value })}
                            rows={3} placeholder="Sua resposta (opcional)" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="sticky bottom-4">
          <Card className="shadow-lg">
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <span className="text-sm text-slate-600">{respondidasObrig()}/{totalObrig()} obrigatórias respondidas</span>
              <Button onClick={enviar} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Enviar respostas
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const Likert = ({ pergunta, labels, valor, onChange }) => (
  <div className="border-b border-slate-100 pb-3">
    <p className="text-sm text-slate-800 mb-2">
      {pergunta.texto}
      {pergunta.obrigatoria !== false && <span className="text-red-500 ml-1">*</span>}
    </p>
    <div className="flex flex-wrap gap-2">
      {[1, 2, 3, 4, 5].map(v => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`flex-1 min-w-[90px] text-xs rounded-md border px-2 py-2 transition-colors ${
            Number(valor) === v
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
          }`}>
          <span className="font-semibold block">{v}</span>
          <span className="block">{labels[v - 1]}</span>
        </button>
      ))}
    </div>
  </div>
);

export default PublicGridMindResponse;
